/**
 * Versioned HTTP contract — `v1` — for DURABLE PERSISTENCE through the secure API
 * gateway (`services/idea-engine-api`), backing the web app's `IdeaRepository` port (DEVOP-12).
 *
 * WHY here (same reasoning as {@link ./v1.ts}): the api service and the web app must
 * parse the SAME request/response shapes without importing one another (ADR 0001).
 * So the wire contract lives in this package as Zod schemas and BOTH sides "parse,
 * don't validate" against it (ADR 0005). The browser NEVER talks to the database —
 * it only ever sees these `/v1/ideas/*` shapes; the Supabase key + SDK live solely in
 * `services/idea-engine-api` (ADR 0028).
 *
 * Shape of the persisted payloads (`IdeaPayloadSchema` / `StageVersionPayloadSchema`)
 * mirrors the web boundary in `services/idea-engine/src/core/repository-validation.ts`:
 * `stages`/`verdict` are validated STRUCTURALLY (object-shaped) because the deep
 * 12-stage math schema is the engine's job — this stays a transport schema, the engine
 * re-parses at its own boundary. Owner identity (`ownerId`) rides in the request body
 * as a single-owner stopgap until DEVOP-4 supplies it from the Access JWT.
 *
 * Endpoints are RPC-style POSTs (owner + params in the body) to match the existing
 * `/v1/generate|provenance|evidence` shape and because the owner stopgap travels in
 * the body, not a header/JWT yet. Breaking changes ship as `v2`, never by mutation.
 */
import { z } from 'zod';
import { apiEnvelope , ModelSelectionSchema } from './v1.js';

// ── Shared field shapes ──────────────────────────────────────────────────────

/** A non-empty, bounded id/owner string (matches the gateway's 64-char id bound). */
const id = z.string().min(1).max(64);
/**
 * Owner identity formerly carried on every request. A6b (IDEA-79) — staged contract
 * transition, step 1: `ownerId` is now OPTIONAL and **accepted-but-ignored** server-side.
 * The gateway authorizes every persisted route on the DERIVED owner (the verified Access
 * identity, `c.get('derivedOwner')`), NEVER this body field, so a client may send it (older
 * clients still do — NON-BREAKING) or omit it. Step 2 (a later increment) drops the field
 * entirely as `persistence-v2` once every client is off it (backlog N105). The RESPONSE
 * payloads keep a REQUIRED, server-stamped `ownerId` (that echoes the row owner) — unchanged.
 */
export const OwnerScopeSchema = z.object({ ownerId: id.optional() });

/** Bounded human strings persisted on an idea / snapshot. */
const title = z.string().min(1).max(200);
const brief = z.string().min(1).max(4000);
const label = z.string().min(1).max(120);

/**
 * Structural guard for the per-stage cells, verdict, and spec: an optional object map.
 * The deep shape is the engine's boundary job (mirrors `repository-validation.ts`), so
 * the wire contract only keeps malformed JSON out.
 */
const stageMap = z.record(z.unknown()).optional();
const verdictMap = z.record(z.unknown()).optional();
const specMap = z.record(z.unknown()).optional();
/**
 * Structural guard for cached evidence (schema v4, ADR 0034), keyed by
 * `"<stageKey>.<field>"`. Same rationale as `stageMap` — the deep citation shape is
 * re-parsed at the engine boundary; the wire only keeps malformed JSON out. Listed
 * explicitly because `z.object` strips unknown keys.
 */
const evidenceMap = z.record(z.unknown()).optional();
/**
 * Structural guard for per-stage explanations (schema v5, ADR 0035), keyed by stage. Same
 * rationale as `stageMap` — the deep Q&A shape is re-parsed at the engine boundary; the wire
 * only keeps malformed JSON out. Listed explicitly because `z.object` strips unknown keys.
 */
const explanationMap = z.record(z.unknown()).optional();
/**
 * Structural guard for per-stage insights (schema v6, ADR 0036), keyed by stage. Same
 * rationale as `stageMap` — the deep analysis shape is re-parsed at the engine boundary; the
 * wire only keeps malformed JSON out. Listed explicitly because `z.object` strips unknown keys.
 */
const insightMap = z.record(z.unknown()).optional();

// ── Persisted payloads (response shapes) ─────────────────────────────────────

/** Wire shape of a persisted Idea row (parsed on read by both sides). */
export const IdeaPayloadSchema = z.object({
  id,
  ownerId: id,
  schemaVersion: z.number().int().positive(),
  title,
  brief,
  stages: stageMap,
  verdict: verdictMap,
  /** Soft-delete marker (IDEA-25): epoch ms when deleted, absent when live. */
  deletedAt: z.number().int().nonnegative().optional(),
  /** Cached evidence (schema v4, ADR 0034), keyed by `"<stageKey>.<field>"`. */
  evidence: evidenceMap,
  /** Generated spec (IDEA-18): persisted after first generation; absent on un-specified ideas. */
  spec: specMap,
  /** Per-stage plain-language Q&A explanations (schema v5, ADR 0035), keyed by stage. */
  explanations: explanationMap,
  /** Per-stage per-sub-metric insights (schema v6, ADR 0036), keyed by stage. */
  insights: insightMap,
  /**
   * Human "real need" confirmation (schema v11, IDEA-62): the operator ticks it in the evaluation.
   * Listed explicitly because `z.object` strips unknown keys — it must survive the wire round-trip.
   */
  needConfirmed: z.boolean().optional(),
  /**
   * Structured real-need assessment (schema v12, IDEA-62). Structural guard — the deep considerations
   * shape is re-parsed at the engine boundary; listed explicitly because `z.object` strips unknown keys.
   */
  needAssessment: z.record(z.unknown()).optional(),
});
export type IdeaPayload = z.infer<typeof IdeaPayloadSchema>;

/** Wire shape of a persisted append-only stage-version snapshot. */
export const StageVersionPayloadSchema = z.object({
  id,
  ideaId: id,
  ownerId: id,
  schemaVersion: z.number().int().positive(),
  createdAt: z.number().int().nonnegative(),
  label,
  title,
  brief,
  stages: stageMap,
  verdict: verdictMap,
  insights: z.record(z.unknown()).optional(),
  steer: z.string().min(1).max(2000).optional(),
  modelSelection: ModelSelectionSchema.optional(),
  correlationId: z.string().min(1).max(64).optional(),
  restoredFrom: id.optional(),
});
export type StageVersionPayload = z.infer<typeof StageVersionPayloadSchema>;

/** Wire shape of a persisted append-only spec-version snapshot (IDEA-28). */
export const SpecVersionPayloadSchema = z.object({
  id,
  ideaId: id,
  ownerId: id,
  schemaVersion: z.number().int().positive(),
  createdAt: z.number().int().nonnegative(),
  label,
  /** Full Specification object — opaque structural guard at the wire layer. */
  spec: z.record(z.unknown()),
  /** The operator feedback that produced this version; absent on initial generation. */
  refinementPrompt: z.string().min(1).optional(),
  restoredFrom: id.optional(),
});
export type SpecVersionPayload = z.infer<typeof SpecVersionPayloadSchema>;

// ── Caller inputs (request sub-shapes) ───────────────────────────────────────

/** Fields a caller supplies to create an idea; the server stamps id/owner/version. */
export const NewIdeaSchema = z.object({
  id: id.optional(),
  title,
  brief,
  stages: stageMap,
  verdict: verdictMap,
  evidence: evidenceMap,
});
export type NewIdeaRequest = z.infer<typeof NewIdeaSchema>;

/** Fields a caller supplies to snapshot an idea into history. */
export const NewStageVersionSchema = z.object({
  label,
  title,
  brief,
  stages: stageMap,
  verdict: verdictMap,
  insights: z.record(z.unknown()).optional(),
  steer: z.string().min(1).max(2000).optional(),
  modelSelection: ModelSelectionSchema.optional(),
  correlationId: z.string().min(1).max(64).optional(),
});
export type NewStageVersionRequest = z.infer<typeof NewStageVersionSchema>;

// ── POST /v1/ideas/list ──────────────────────────────────────────────────────

/** Request: list the owner's ideas (newest first). */
export const ListIdeasRequestSchema = OwnerScopeSchema;
export type ListIdeasRequest = z.infer<typeof ListIdeasRequestSchema>;
/** Response: the owner's ideas. */
export const ListIdeasResponseSchema = apiEnvelope(z.array(IdeaPayloadSchema));
export type ListIdeasResponse = z.infer<typeof ListIdeasResponseSchema>;

// ── POST /v1/ideas/get ────────────────────────────────────────────────────────

/** Request: fetch one owned idea by id. */
export const GetIdeaRequestSchema = OwnerScopeSchema.extend({ id });
export type GetIdeaRequest = z.infer<typeof GetIdeaRequestSchema>;
/** Response: a single idea. */
export const IdeaResponseSchema = apiEnvelope(IdeaPayloadSchema);
export type IdeaResponse = z.infer<typeof IdeaResponseSchema>;

// ── POST /v1/ideas/create ───────────────────────────────────────────────────────

/** Request: persist a new idea (server stamps ownerId/schemaVersion/id). */
export const CreateIdeaRequestSchema = OwnerScopeSchema.extend({ idea: NewIdeaSchema });
export type CreateIdeaRequest = z.infer<typeof CreateIdeaRequestSchema>;
// Response: `IdeaResponseSchema` (a single idea).

// ── POST /v1/ideas/update ────────────────────────────────────────────────────

/** Request: replace an existing owned idea in place. */
export const UpdateIdeaRequestSchema = OwnerScopeSchema.extend({ idea: IdeaPayloadSchema });
export type UpdateIdeaRequest = z.infer<typeof UpdateIdeaRequestSchema>;
// Response: `IdeaResponseSchema` (the stored idea).

// ── POST /v1/ideas/versions/save ──────────────────────────────────────────────

/** Request: append an immutable snapshot to an idea's history. */
export const SaveStageVersionRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  version: NewStageVersionSchema,
});
export type SaveStageVersionRequest = z.infer<typeof SaveStageVersionRequestSchema>;
/** Response: the stored snapshot. */
export const StageVersionResponseSchema = apiEnvelope(StageVersionPayloadSchema);
export type StageVersionResponse = z.infer<typeof StageVersionResponseSchema>;

// ── POST /v1/ideas/versions/list ──────────────────────────────────────────────

/** Request: list an idea's snapshot history (newest first). */
export const ListStageVersionsRequestSchema = OwnerScopeSchema.extend({ ideaId: id });
export type ListStageVersionsRequest = z.infer<typeof ListStageVersionsRequestSchema>;
/** Response: the idea's snapshots. */
export const ListStageVersionsResponseSchema = apiEnvelope(z.array(StageVersionPayloadSchema));
export type ListStageVersionsResponse = z.infer<typeof ListStageVersionsResponseSchema>;

// ── POST /v1/ideas/versions/restore ───────────────────────────────────────────

/** Request: restore an earlier snapshot (appends a NEW version; non-destructive). */
export const RestoreStageVersionRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  versionId: id,
});
export type RestoreStageVersionRequest = z.infer<typeof RestoreStageVersionRequestSchema>;
// Response: `StageVersionResponseSchema` (the freshly appended version).

// ── POST /v1/ideas/delete · /v1/ideas/restore (soft delete — IDEA-25) ──────────

/**
 * Request: SOFT-delete one owned idea — set `deletedAt`, hide it from the portfolio,
 * but keep it recoverable (nothing is hard-removed). 404 when the owner has no such
 * (live) idea.
 */
export const DeleteIdeaRequestSchema = OwnerScopeSchema.extend({ id });
export type DeleteIdeaRequest = z.infer<typeof DeleteIdeaRequestSchema>;
/** Response: the id that was soft-deleted (drives the Undo affordance). */
export const DeleteIdeaResponseSchema = apiEnvelope(z.object({ id }));
export type DeleteIdeaResponse = z.infer<typeof DeleteIdeaResponseSchema>;

/** Request: restore (un-delete) a previously soft-deleted owned idea. */
export const RestoreIdeaRequestSchema = OwnerScopeSchema.extend({ id });
export type RestoreIdeaRequest = z.infer<typeof RestoreIdeaRequestSchema>;
/** Response: the restored idea (now live again). */
export const RestoreIdeaResponseSchema = apiEnvelope(IdeaPayloadSchema);
export type RestoreIdeaResponse = z.infer<typeof RestoreIdeaResponseSchema>;

// ── POST /v1/ideas/spec-versions/* (IDEA-28) ─────────────────────────────────

/** Shared response schema for a single spec version. */
export const SpecVersionResponseSchema = apiEnvelope(SpecVersionPayloadSchema);
export type SpecVersionResponse = z.infer<typeof SpecVersionResponseSchema>;

/** Request: append an immutable spec snapshot. */
export const SaveSpecVersionRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  label,
  spec: z.record(z.unknown()),
  refinementPrompt: z.string().min(1).optional(),
});
export type SaveSpecVersionRequest = z.infer<typeof SaveSpecVersionRequestSchema>;

/** Request: list an idea's spec version history. */
export const ListSpecVersionsRequestSchema = OwnerScopeSchema.extend({ ideaId: id });
export type ListSpecVersionsRequest = z.infer<typeof ListSpecVersionsRequestSchema>;
/** Response: the idea's spec versions. */
export const ListSpecVersionsResponseSchema = apiEnvelope(z.array(SpecVersionPayloadSchema));
export type ListSpecVersionsResponse = z.infer<typeof ListSpecVersionsResponseSchema>;

/** Request: restore an earlier spec version (non-destructive: appends a new row). */
export const RestoreSpecVersionRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  versionId: id,
});
export type RestoreSpecVersionRequest = z.infer<typeof RestoreSpecVersionRequestSchema>;
